import { LineChart as EChartsLineChart } from "echarts/charts";
import {
    GridComponent,
    LegendComponent,
    TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
    ECBasicOption,
    EChartsType,
} from "echarts/types/dist/shared";
import React, {
    CSSProperties,
    memo,
    useEffect,
    useRef,
} from "react";

echarts.use([
    EChartsLineChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    CanvasRenderer,
]);

export type ChartEventHandler = (params: any) => void;

export interface ILineChartProps {
    option: ECBasicOption;

    height?: number | string;
    width?: number | string;

    className?: string;
    style?: CSSProperties;

    notMerge?: boolean;
    lazyUpdate?: boolean;

    events?: Record<string, ChartEventHandler>;

    onReady?: (chart: EChartsType) => void;
}

const LineChart: React.FC<ILineChartProps> = memo(({
    option,
    height = 350,
    width = "100%",
    className,
    style,
    notMerge = false,
    lazyUpdate = true,
    events,
    onReady,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<EChartsType>();
    const eventsRef = useRef(events);
    const onReadyRef = useRef(onReady);

    /*
     * Lưu callback mới nhất mà không cần khởi tạo lại chart.
     */
    useEffect(() => {
        eventsRef.current = events;
    }, [events]);

    useEffect(() => {
        onReadyRef.current = onReady;
    }, [onReady]);

    /*
     * Khởi tạo chart một lần.
     */
    useEffect(() => {
        const element = rootRef.current;

        if (!element) return;

        const existingInstance = echarts.getInstanceByDom(element);

        if (existingInstance) {
            existingInstance.dispose();
        }

        const chart = echarts.init(element);

        chartRef.current = chart;
        onReadyRef.current?.(chart);

        /*
         * Resize chart khi container thay đổi kích thước.
         */
        const resizeObserver = new ResizeObserver(() => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();

            if (!chart.isDisposed()) {
                chart.dispose();
            }

            chartRef.current = undefined;
        };
    }, []);

    /*
     * Đăng ký lại events khi danh sách event thay đổi.
     */
    useEffect(() => {
        const chart = chartRef.current;

        if (!chart || chart.isDisposed()) return;

        const currentEvents = eventsRef.current ?? {};

        Object.entries(currentEvents).forEach(([eventName, handler]) => {
            chart.off(eventName);
            chart.on(eventName, handler);
        });

        return () => {
            if (!chart.isDisposed()) {
                Object.keys(currentEvents).forEach(eventName => {
                    chart.off(eventName);
                });
            }
        };
    }, [events]);

    /*
     * Update option mà không khởi tạo lại chart.
     */
    useEffect(() => {
        const chart = chartRef.current;

        if (!chart || chart.isDisposed()) return;

        chart.setOption(option, { notMerge, lazyUpdate, });
    }, [option, notMerge, lazyUpdate,]);

    return (<div ref={rootRef} className={className} style={{ width, height, ...style, }} />);
});

LineChart.displayName = "LineChart";

export default LineChart;
